import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { ModuleLayout } from "../../../layouts";
import { ContentCard, Button, FormField, Loader } from "../../../components";
import { ROUTES_CONSTANTS, QUERY_CONSTANTS } from "../../../constants";
import { useAuth } from "../../../context";
import {
  inviteUserApi,
  getUserByIdApi,
  updateUserApi,
  getPermissionGroupsApi,
} from "../../../DAL";

const UserForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const companyId = currentUser?.company?.id;

  const isEdit = location.pathname === ROUTES_CONSTANTS.UPDATE_USER;
  const userId = location.state?.id;

  // Redirect if edit hit directly without ID
  useEffect(() => {
    if (isEdit && !userId) {
      navigate(ROUTES_CONSTANTS.USERS, { replace: true });
    }
  }, [isEdit, userId, navigate]);

  const methods = useForm({
    defaultValues: { name: "", email: "", permission_groups: [] },
  });
  const { reset } = methods;

  // ── Permission groups LOV ────────────────────────────────────────────────────
  const { data: groupsData } = useQuery({
    queryKey: [QUERY_CONSTANTS.PERMISSION_GROUPS],
    queryFn: getPermissionGroupsApi,
  });
  const groupOptions = (groupsData?.data ?? []).map((g) => ({
    value: g.id,
    label: g.display_name || g.name,
  }));

  // ── Fetch user for edit mode ─────────────────────────────────────────────────
  const { data: userData, isLoading: isLoadingUser } = useQuery({
    queryKey: [QUERY_CONSTANTS.USER, userId],
    queryFn: async () => {
      const res = await getUserByIdApi(companyId, userId);
      return res.data;
    },
    enabled: isEdit && !!userId && !!companyId,
  });

  useEffect(() => {
    if (!isEdit || !userData) return;
    reset({
      name: userData.name || "",
      email: userData.email || "",
      permission_groups: (userData.permission_groups ?? []).map((g) => g.id),
    });
  }, [isEdit, userData, reset]);

  // ── Mutation ─────────────────────────────────────────────────────────────────
  const { mutate: submitUser, isPending } = useMutation({
    mutationFn: (data) => {
      const payload = {
        name: data.name,
        email: data.email,
        permission_groups: data.permission_groups
          ? [Number(data.permission_groups)]
          : [],
      };
      return isEdit
        ? updateUserApi(companyId, userId, payload)
        : inviteUserApi(companyId, payload);
    },
    onSuccess: (response) => {
      enqueueSnackbar(response?.message, { variant: "success" });
      queryClient.invalidateQueries({ queryKey: [QUERY_CONSTANTS.USERS] });
      navigate(ROUTES_CONSTANTS.USERS);
    },
  });

  const onSubmit = (data) => submitUser(data);

  if (isEdit && isLoadingUser) return <Loader />;

  return (
    <ModuleLayout
      title={t('users.title')}
      onBack={() => navigate(ROUTES_CONSTANTS.USERS)}
    >
      <p className="text-sm text-gray-500 mb-4">
        {isEdit ? t('users.breadcrumbEdit') : t('users.breadcrumbInvite')}
      </p>

      <ContentCard>
        <h2 className="text-2xl font-medium text-black mb-1">
          {isEdit ? t('users.editTitle') : t('users.inviteTitle')}
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          {isEdit
            ? t('users.editSubtitle')
            : t('users.inviteSubtitle')}
        </p>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <FormField
                name="name"
                label="Full Name"
                placeholder="Enter Full Name"
              />
              <FormField
                name="email"
                type="email"
                label="Email Address"
                placeholder="Enter Email Address"
                disabled={isEdit}
              />
            </div>

            <FormField
              name="permission_groups"
              type="select"
              label="Permission Group"
              placeholder="Select Permission Group"
              options={groupOptions}
            />
          </form>
        </FormProvider>
      </ContentCard>

      <div className="flex justify-end gap-3 mt-4">
        <Button
          variant="secondary"
          size="lg"
          onClick={() => navigate(ROUTES_CONSTANTS.USERS)}
        >
          {t('common.cancel')}
        </Button>
        <Button
          size="lg"
          loading={isPending}
          onClick={methods.handleSubmit(onSubmit)}
        >
          {isEdit ? t('users.btnUpdate') : t('users.btnInvite')}
        </Button>
      </div>
    </ModuleLayout>
  );
};

export default UserForm;
